import { requestWithFallback } from '../httpClient';

const VISITAS_LIST_CACHE_TTL_MS = 30000;
const VISITAS_LIST_CACHE_MAX_ENTRIES = 20;
const VISITAS_LIST_CACHE_MAX_BYTES = 2 * 1024 * 1024;
const visitasListCache = new Map();
let visitasListCacheBytes = 0;
let visitasListCacheOwner = null;
let visitasListCacheGeneration = 0;

function stableSerialize(value) {
    if (value === null || value === undefined) {
        return JSON.stringify(value) ?? 'undefined';
    }

    if (Array.isArray(value)) {
        return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
    }

    if (typeof value === 'object') {
        return `{${Object.keys(value)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
            .join(',')}}`;
    }

    return JSON.stringify(value);
}

function removeListCacheEntry(key) {
    const entry = visitasListCache.get(key);
    if (entry) visitasListCacheBytes -= entry.bytes;
    visitasListCache.delete(key);
}

export function clearVisitasListCache() {
    visitasListCache.clear();
    visitasListCacheBytes = 0;
    visitasListCacheGeneration++;
}

export async function listVisitas(token, params = {}, options = {}) {
    const {
        useCache = true,
        forceRefresh = false,
        cacheTtlMs = VISITAS_LIST_CACHE_TTL_MS,
        cacheScope = null,
    } = options;

    const owner = stableSerialize([token || null, cacheScope]);
    if (owner !== visitasListCacheOwner) {
        clearVisitasListCache();
        visitasListCacheOwner = owner;
    }
    const generation = visitasListCacheGeneration;
    const cacheKey = stableSerialize(params);
    const now = Date.now();
    for (const [key, entry] of visitasListCache) {
        if (entry.expiresAt <= now) removeListCacheEntry(key);
    }

    if (useCache && !forceRefresh) {
        const cacheEntry = visitasListCache.get(cacheKey);
        if (cacheEntry) {
            visitasListCache.delete(cacheKey);
            visitasListCache.set(cacheKey, cacheEntry);
            return cacheEntry.data;
        }
    }

    let result;
    try {
        result = await requestWithFallback({
            moduleName: 'visitas',
            endpoint: 'list',
            endpointIsKey: true,
            method: 'get',
            token,
            params,
            // Una lectura anterior a Guardar o al cambio de cuenta no puede llenar la caché nueva.
            dedupeScope: `visitas:list:${owner}:${generation}:${cacheKey}`,
        });
    } catch (error) {
        if ([401, 403].includes(Number(error?.response?.status ?? error?.status))
            && generation === visitasListCacheGeneration && owner === visitasListCacheOwner) {
            clearVisitasListCache();
        }
        throw error;
    }

    if (useCache && generation === visitasListCacheGeneration && owner === visitasListCacheOwner) {
        removeListCacheEntry(cacheKey);
        // Presupuesto estimado de texto UTF-16; no incluye ni elimina datos persistentes offline.
        let bytes = Infinity;
        try { bytes = (cacheKey.length + JSON.stringify(result.data).length) * 2; } catch { /* La respuesta sigue siendo válida sin caché. */ }
        if (bytes <= VISITAS_LIST_CACHE_MAX_BYTES && Number.isFinite(cacheTtlMs) && cacheTtlMs > 0) {
            visitasListCache.set(cacheKey, { data: result.data, bytes, expiresAt: Date.now() + cacheTtlMs });
            visitasListCacheBytes += bytes;
            while (visitasListCache.size > VISITAS_LIST_CACHE_MAX_ENTRIES || visitasListCacheBytes > VISITAS_LIST_CACHE_MAX_BYTES) {
                removeListCacheEntry(visitasListCache.keys().next().value);
            }
        }
    }

    return result.data;
}

export async function getVisitaStatus(token, visitaId) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'status',
        endpointIsKey: true,
        method: 'get',
        token,
        pathParams: { id: visitaId },
    });

    return result.data;
}

export async function getVisitaDetail(token, visitaId) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'detail',
        endpointIsKey: true,
        method: 'get',
        token,
        pathParams: { id: visitaId },
    });

    return result.data;
}

export async function downloadVisitaOfflineDocument(token, visitaId, document) {
    const response = await requestWithFallback({ moduleName: 'visitas', endpoint: 'offlineDocument', endpointIsKey: true,
        method: 'get', token, pathParams: { id: visitaId, uuid: document.client_uuid },
        params: { sha256: document.file_sha256, size: document.size }, responseType: 'arraybuffer', timeout: 30000 });
    return new Uint8Array(response.data);
}

export async function downloadVisitaOfflinePhoto(token, visitaId, photo) {
    const response = await requestWithFallback({ moduleName: 'visitas', endpoint: 'offlinePhoto', endpointIsKey: true,
        method: 'get', token, pathParams: { id: visitaId, uuid: photo.client_uuid },
        params: { sha256: photo.file_sha256, size: photo.size }, responseType: 'arraybuffer', timeout: 30000 });
    return new Uint8Array(response.data);
}

export async function getVisitaContextRevisions(token, visitaIds) {
    const response = await requestWithFallback({moduleName:'visitas',endpoint:'offlineContextRevisions',endpointIsKey:true,method:'post',token,data:{visita_ids:visitaIds}});
    return response.data;
}

export async function getVisitaEditContext(token, visitaId, {forOffline=false}={}) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: forOffline?'offlineContext':'editContext',
        endpointIsKey: true,
        method: 'get',
        token,
        pathParams: { id: visitaId },
        timeout: 45000,
    });

    if(result.data?.visita?.offline_merge_pilot&&['pendiente','iniciada'].includes(result.data.visita.estado)){
        const {getVisitaOfflineEntry}=await import('../../services/visitaOfflineQueue.js');
        const actor=result.data.offline_authorization?.actor;
        if(actor?.id&&!await getVisitaOfflineEntry({userId:actor.id,visitaId:Number(visitaId)})){
            const prepared=result.data.offline_merge_base||await autosaveVisita(token,visitaId,null,{mergeAction:'prepare',expectedVersion:result.data.visita.responses_version,formRevision:result.data.offline_authorization.form_revision,snapshotHash:result.data.visita.offline_merge_snapshot_hash});
            await (await import('../../services/visitaMergeSync.js')).saveMergeBase(prepared,{userId:actor.id,empresaId:actor.empresa_id,visitaId:Number(visitaId),responsesVersion:result.data.visita.responses_version,formRevision:result.data.offline_authorization.form_revision,snapshotHash:result.data.visita.offline_merge_snapshot_hash});
        }
    }
    return result.data;
}

function documentationPayload(response) {
    const payload = Array.isArray(response?.adjuntos_documentacion) ? response : response?.data;
    if (!Array.isArray(payload?.adjuntos_documentacion)) {
        throw new Error('El servidor no confirmó la documentación. Se conserva la selección para revisar.');
    }
    return payload;
}

export async function uploadVisitaDocumentation(token, visitaId, files) {
    const formData = new FormData();
    (files || []).forEach((file) => formData.append('archivos[]', file));
    const result = await requestWithFallback({
        moduleName: 'visitas', endpoint: 'uploadDocumentation', endpointIsKey: true,
        method: 'post', token, pathParams: { id: visitaId }, data: formData,
    });
    return documentationPayload(result.data);
}

export async function deleteVisitaDocumentation(token, visitaId, index, {expectedPath} = {}) {
    const result = await requestWithFallback({
        moduleName: 'visitas', endpoint: 'deleteDocumentation', endpointIsKey: true,
        method: 'post', token, pathParams: { id: visitaId }, data: { index, ...(expectedPath?{expected_path:expectedPath}:{}) },
    });
    return documentationPayload(result.data);
}

export async function autosaveVisita(token, visitaId, payload, options = {}) {
    clearVisitasListCache();
    if (options.mergeAction) {
        const action=options.mergeAction;
        if (action === 'copy') return getVisitaEditContext(token, visitaId, {forOffline:true});
        const endpoint={submit:'mergeSubmit',prepare:'mergePrepare',status:'mergeStatus'}[action];
        if(!endpoint)throw Error('La acción de merge no está admitida.');
        const result=await requestWithFallback({moduleName:'visitas',endpoint,endpointIsKey:true,pathParams:{id:visitaId},method:action==='status'?'get':'post',token,
            data:action==='submit'?payload:action==='prepare'?{expected_version:options.expectedVersion,form_revision:options.formRevision,snapshot_hash:options.snapshotHash}:undefined,headers:action==='submit'?{'Content-Type':'application/json'}:undefined,dedupeInFlight:false});
        return result.data;
    }

    const headers = {};
    if (options?.idempotencyKey) {
        headers['X-Idempotency-Key'] = String(options.idempotencyKey);
    }
    if (options?.revisionId) {
        headers['X-Draft-Revision-Id'] = String(options.revisionId);
    }

    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'autosave',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: payload,
        headers: Object.keys(headers).length > 0 ? headers : undefined,
    });

    return result.data;
}

export async function addVisitaGroup(token, visitaId, payload, options = {}) {
    const rawPayload = typeof payload === 'string'
        ? (() => { try { return JSON.parse(payload); } catch { return {}; } })()
        : (payload || {});
    const resolvedClientUuid = rawPayload?.client_uuid || options?.client_uuid || (typeof options?.idempotencyKey === 'string' && options.idempotencyKey.startsWith('operation:') ? options.idempotencyKey.replace('operation:', '') : undefined);
    const idempotencyKey = options?.idempotencyKey
        ? String(options.idempotencyKey)
        : (resolvedClientUuid ? `operation:${resolvedClientUuid}` : undefined);

    const finalPayload = {
        ...rawPayload,
        ...(resolvedClientUuid ? { client_uuid: resolvedClientUuid } : {}),
    };

    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'addGroup',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: finalPayload,
        headers: idempotencyKey
            ? { 'X-Idempotency-Key': idempotencyKey }
            : undefined,
    });

    return result.data;
}

export async function dividirTrabajoVisita(token, visitaId, payload = {}, options = {}) {
    const rawPayload = typeof payload === 'string'
        ? (() => { try { return JSON.parse(payload); } catch { return {}; } })()
        : (payload || {});
    const idempotencyKey = options?.idempotencyKey
        ? String(options.idempotencyKey)
        : (options?.client_uuid ? `operation:${options.client_uuid}` : undefined);
    const clientUuid = options?.client_uuid || rawPayload?.client_uuid || (typeof idempotencyKey === 'string' && idempotencyKey.startsWith('operation:') ? idempotencyKey.replace('operation:', '') : undefined);

    const finalPayload = {
        ...rawPayload,
        ...(clientUuid ? { client_uuid: clientUuid } : {}),
    };

    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'dividirTrabajo',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: finalPayload,
        headers: idempotencyKey
            ? { 'X-Idempotency-Key': idempotencyKey }
            : undefined,
    });

    return result.data;
}

export async function finalizarMiSeccionVisita(token, visitaId, payload = {}) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'finalizarMiSeccion',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: payload,
    });

    return result.data;
}

export async function removeVisitaGroupInstance(token, visitaId, payload, options = {}) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'removeGroupInstance',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: payload,
        headers: options.idempotencyKey ? { 'X-Idempotency-Key': options.idempotencyKey } : undefined,
    });

    return result.data;
}

export async function uploadVisitaAttachment(token, visitaId, file, command) {
    const manifest = JSON.parse(command.manifest);
    if (!['image', 'document'].includes(manifest.kind) || manifest.visita_id !== Number(visitaId)
        || command.idempotencyKey !== `attachment:${manifest.client_uuid}`) throw Error('La intención de subida no corresponde a este archivo.');
    const formData = new FormData();
    formData.append(manifest.kind === 'document' ? 'archivos[]' : 'image', file);
    formData.append('upload_contract', '2');
    formData.append('manifest', command.manifest);
    const result = await requestWithFallback({ moduleName: 'visitas', endpoint: manifest.kind === 'document' ? 'uploadDocumentation' : 'uploadImage', endpointIsKey: true,
        method: 'post', token, pathParams: { id: visitaId }, data: formData,
        headers: { 'X-Idempotency-Key': command.idempotencyKey }, returnFullResponse: true });
    return result.data;
}

export async function uploadVisitaImage(token, visitaId, file, descripcion = '', metadata = {}, options = {}) {
    if (options.attachmentCommand) {
        if (JSON.parse(options.attachmentCommand.manifest).kind !== 'image') throw Error('La intención no corresponde a una foto.');
        return uploadVisitaAttachment(token, visitaId, file, options.attachmentCommand);
    }
    const formData = new FormData();
    formData.append('image', file);
    formData.append('descripcion', descripcion || '');
    formData.append('instance_ref', metadata?.instanceRef || '');
    formData.append('instance_label', metadata?.instanceLabel || '');
    if (metadata?.gpsLat !== undefined && metadata?.gpsLat !== null) {
        formData.append('gps_lat', String(metadata.gpsLat));
    }
    if (metadata?.gpsLng !== undefined && metadata?.gpsLng !== null) {
        formData.append('gps_lng', String(metadata.gpsLng));
    }
    if (metadata?.gpsAccuracy !== undefined && metadata?.gpsAccuracy !== null) {
        formData.append('gps_accuracy', String(metadata.gpsAccuracy));
    }
    if (metadata?.gpsCapturedAt) {
        formData.append('gps_captured_at', String(metadata.gpsCapturedAt));
    }

    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'uploadImage',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: formData,
        headers: options?.idempotencyKey
            ? { 'X-Idempotency-Key': String(options.idempotencyKey) }
            : undefined,
    });

    return result.data;
}

export async function deleteVisitaImage(token, visitaId, index) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'deleteImage',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: { index },
    });

    return result.data;
}

export async function reorderVisitaImages(token, visitaId, payload) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'reorderImages',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: payload,
    });

    return result.data;
}

export async function reconcileVisitaFinalization(token, visitaId, payload) {
    const result = await requestWithFallback({ moduleName: 'visitas', endpoint: 'reconcileFinalization', endpointIsKey: true,
        method: 'post', token, pathParams: { id: visitaId }, data: payload, returnFullResponse: true, timeout: 45000 });
    if (result.data?.status !== 200) throw new Error('El servidor no devolvió una conciliación definitiva.');
    return result.data.data;
}

export async function finalizarVisita(token, visitaId, payload, options = {}) {
    clearVisitasListCache();

    const idempotencyKey = options?.idempotencyKey
        ? String(options.idempotencyKey)
        : (options?.client_uuid ? `operation:${options.client_uuid}` : undefined);
    const clientUuid = options?.client_uuid || (typeof idempotencyKey === 'string' && idempotencyKey.startsWith('operation:') ? idempotencyKey.replace('operation:', '') : undefined);

    const finalPayload = {
        ...(payload || {}),
        ...(clientUuid ? { client_uuid: clientUuid } : {}),
    };

    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'finish',
        returnFullResponse: true,
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: finalPayload,
        timeout: finalPayload.finalization_contract === 2 ? 45000 : 15000,
        headers: idempotencyKey
            ? { 'X-Idempotency-Key': idempotencyKey }
            : undefined,
    });

    return { ...result.data.data, _httpStatus: result.data.status };
}

export async function saveVisitaObservacionesCorrectivas(token, visitaId, payload) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'saveObservacionesCorrectivas',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: payload,
    });

    return result.data;
}

export async function getActivosCatalogoBySucursal(token, sucursalId) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'catalogBySucursal',
        endpointIsKey: true,
        method: 'get',
        token,
        pathParams: { sucursalId },
    });

    return result.data;
}

export async function getActivosCatalogoMisSucursales(token, userId) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'catalogAllByUser',
        endpointIsKey: true,
        method: 'get',
        token,
        params: {
            user_id: userId,
        },
    });

    return result.data;
}

export async function resolveActivoQr(token, payload) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'resolveActivoQr',
        endpointIsKey: true,
        method: 'post',
        token,
        data: payload,
    });

    return result.data;
}

export async function toggleVisitaInstance(token, visitaId, payload) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'toggleInstance',
        endpointIsKey: true,
        method: 'patch',
        token,
        pathParams: { id: visitaId },
        data: payload,
    });

    return result.data;
}

export async function updateVisitaState(token, visitaId, payload) {
    clearVisitasListCache();

    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'updateState',
        endpointIsKey: true,
        method: 'patch',
        token,
        pathParams: { id: visitaId },
        data: payload,
    });

    return result.data;
}

export async function startPreparedVisita(token, visitaId, payload, options) {
    clearVisitasListCache();
    const result = await requestWithFallback({moduleName:'visitas',endpoint:'updateState',endpointIsKey:true,
        method:'patch',token,pathParams:{id:visitaId},data:{...payload,client_uuid:options.client_uuid},
        headers:{'X-Idempotency-Key':options.idempotencyKey},returnFullResponse:true});
    return {...result.data.data,_httpStatus:result.data.status};
}

export async function getVisitaInformePreview(token, visitaId) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'reportPreview',
        endpointIsKey: true,
        method: 'get',
        timeout: 45000,
        token,
        pathParams: { id: visitaId },
    });

    let backendOrigin = '';
    try {
        if (result?.meta?.url) {
            backendOrigin = new URL(result.meta.url).origin;
        }
    } catch {
        backendOrigin = '';
    }

    return {
        ...result.data,
        backend_origin: backendOrigin,
    };
}

export async function prepararVisitaParaControl(token, visitaId, expectedVersion) {
    const result = await requestWithFallback({
        moduleName: 'visitas', endpoint: 'prepareControl', endpointIsKey: true,
        method: 'post', token, pathParams: { id: visitaId },
        data: { expected_version: expectedVersion ?? null },
    });
    return result.data;
}

export async function updateVisitaInformeBody(token, visitaId, payload) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'updateReportBody',
        endpointIsKey: true,
        method: 'patch',
        token,
        pathParams: { id: visitaId },
        data: payload,
    });

    return result.data;
}

export async function restoreVisitaInformeBody(token, visitaId) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'restoreReportBody',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: {},
    });

    return result.data;
}

export async function downloadVisitaPdf(token, visitaId) {
    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'pdf',
        endpointIsKey: true,
        method: 'get',
        token,
        pathParams: { id: visitaId },
        responseType: 'blob',
    });

    return result.data;
}

export async function generarComprobanteServicio(token, visitaId, payload = {}, options = {}) {
    const idempotencyKey = options?.idempotencyKey
        ? String(options.idempotencyKey)
        : (options?.client_uuid ? `operation:${options.client_uuid}` : undefined);
    const clientUuid = options?.client_uuid || (typeof idempotencyKey === 'string' && idempotencyKey.startsWith('operation:') ? idempotencyKey.replace('operation:', '') : undefined);

    const finalPayload = {
        ...(payload || {}),
        ...(clientUuid ? { client_uuid: clientUuid } : {}),
    };

    const result = await requestWithFallback({
        moduleName: 'visitas',
        endpoint: 'comprobantes',
        endpointIsKey: true,
        method: 'post',
        token,
        pathParams: { id: visitaId },
        data: finalPayload,
        headers: idempotencyKey
            ? { 'X-Idempotency-Key': idempotencyKey }
            : undefined,
    });

    return result.data;
}
